
import { FeatureGuide } from '@/types/knowledgeBase';

// Base admin guides that all admin roles can see
const baseAdminGuides: FeatureGuide[] = [
  {
    id: 'admin-dashboard',
    title: 'Admin Dashboard',
    description: 'Overview of your admin dashboard and key metrics',
    category: 'dashboard',
    content: `
      <h2>Admin Dashboard Overview</h2>
      <p>The admin dashboard provides a central location to view key metrics and manage your business.</p>
      <ul>
        <li><strong>Usage Stats:</strong> Track user activity and subscription utilization</li>
        <li><strong>Quick Actions:</strong> Common tasks you can perform with one click</li>
        <li><strong>Reports:</strong> Access detailed analytics about your business</li>
      </ul>
      <h3>Getting Started</h3>
      <p>When you first log in as an admin, you'll see the dashboard with several sections:</p>
      <ol>
        <li>The top section shows your key metrics like total users, active referrals, and revenue</li>
        <li>The middle section displays usage trends and statistics</li>
        <li>The right sidebar contains quick actions and common tasks</li>
      </ol>
      <p>Click on any card to view more detailed information or take specific actions.</p>
    `,
    lastUpdated: '2024-04-02',
    isNew: false
  },
  {
    id: 'admin-reports',
    title: 'Running Reports',
    description: 'How to generate and analyze business reports',
    category: 'reports',
    content: `
      <h2>Running Reports</h2>
      <p>The Reports section allows you to generate and analyze various aspects of your business.</p>
      <h3>Available Report Types</h3>
      <ul>
        <li><strong>User Activity:</strong> Track user logins, registrations, and engagement</li>
        <li><strong>Referral Performance:</strong> See which referral campaigns are most effective</li>
        <li><strong>Revenue:</strong> Analyze income streams and identify trends</li>
        <li><strong>Payout Reports:</strong> Track all payouts to referrers</li>
      </ul>
      <h3>How to Generate a Report</h3>
      <ol>
        <li>Navigate to the Reports section from the admin menu</li>
        <li>Select the report type you want to generate</li>
        <li>Choose your date range and any other filters</li>
        <li>Click "Generate Report"</li>
        <li>View the results or export to CSV for further analysis</li>
      </ol>
      <h3>Interpreting Report Data</h3>
      <p>Each report includes visual charts and raw data tables. The charts help you identify trends at a glance, while the tables provide detailed information for deeper analysis.</p>
    `,
    lastUpdated: '2024-03-25',
    isNew: false
  },
  {
    id: 'referrer-payments',
    title: 'Managing Referrer Payments',
    description: 'How to review, approve and process referrer payments',
    category: 'payments',
    content: `
      <h2>Managing Referrer Payments</h2>
      <p>The Referrer Payments section allows you to manage all aspects of paying your referral partners.</p>
      <h3>Payment Workflow</h3>
      <ol>
        <li><strong>Review:</strong> Examine pending payments and verify referral validity</li>
        <li><strong>Approve:</strong> Mark verified referrals for payment</li>
        <li><strong>Process:</strong> Send approved payments to your payment provider</li>
        <li><strong>Track:</strong> Monitor payment status and history</li>
      </ol>
      <h3>Step-by-step Payment Approval</h3>
      <ol>
        <li>Navigate to Referrer Payments in the admin menu</li>
        <li>Review the list of pending payments</li>
        <li>Click on any payment to view detailed information about the referral</li>
        <li>Use the "Approve" or "Reject" button to process each payment</li>
        <li>For batch processing, use the checkboxes to select multiple payments</li>
        <li>Click "Process Selected" to approve or reject in bulk</li>
      </ol>
      <h3>Payment History</h3>
      <p>The payment history tab shows all past payments, their status, and details. You can filter by date, status, or referrer to find specific information.</p>
    `,
    lastUpdated: '2024-04-05',
    isNew: true
  },
  {
    id: 'admin-settings',
    title: 'Configuring System Settings',
    description: 'How to customize system settings for your business',
    category: 'settings',
    content: `
      <h2>Configuring System Settings</h2>
      <p>The Settings section allows you to customize how your referral system works.</p>
      <h3>Available Settings</h3>
      <ul>
        <li><strong>Domain Settings:</strong> Configure your custom domain and URL structure</li>
        <li><strong>Email Settings:</strong> Set up email templates and notification preferences</li>
        <li><strong>Integration Settings:</strong> Connect with third-party services</li>
        <li><strong>Payment Settings:</strong> Configure payout methods and thresholds</li>
        <li><strong>User Settings:</strong> Manage user permissions and roles</li>
      </ul>
      <h3>Updating Settings</h3>
      <ol>
        <li>Navigate to the Settings section from the admin menu</li>
        <li>Select the appropriate tab for the settings you want to modify</li>
        <li>Make your changes</li>
        <li>Click "Save" to apply your changes</li>
      </ol>
      <p>Most settings take effect immediately, but some may require a system refresh or cache clear.</p>
    `,
    lastUpdated: '2024-02-15',
    isNew: false
  },
  {
    id: 'business-setup',
    title: 'Business Setup',
    description: 'How to configure your business information and branding',
    category: 'settings',
    content: `
      <h2>Business Setup</h2>
      <p>The Business Setup section allows you to customize your business profile and branding.</p>
      <h3>Key Configuration Areas</h3>
      <ul>
        <li><strong>Company Information:</strong> Update your business name, address, and contact details</li>
        <li><strong>Branding:</strong> Upload your logo and set brand colors</li>
        <li><strong>Legal Documents:</strong> Configure your terms of service and privacy policy</li>
        <li><strong>Payment Details:</strong> Set up your payment processing accounts</li>
      </ul>
      <h3>Step-by-step Branding Setup</h3>
      <ol>
        <li>Navigate to Business Setup in the admin menu</li>
        <li>Upload your company logo (recommended size: 200x50 pixels)</li>
        <li>Select your primary and secondary brand colors</li>
        <li>Preview how your branding will appear to users</li>
        <li>Click "Save Changes" to apply your branding</li>
      </ol>
      <p>Your branding will be applied to all user-facing pages, emails, and documents.</p>
    `,
    lastUpdated: '2024-03-10',
    isNew: false
  }
];

// Super admin specific guides
const superAdminGuides: FeatureGuide[] = [
  {
    id: 'instance-management',
    title: 'Managing SaaS Instances',
    description: 'How to create and manage customer SaaS instances',
    category: 'instances',
    content: `
      <h2>Managing SaaS Instances</h2>
      <p>As a super admin, you can create and manage customer SaaS instances across your platform.</p>
      <h3>Instance Management Operations</h3>
      <ul>
        <li><strong>Create Instance:</strong> Set up a new customer SaaS environment</li>
        <li><strong>Configure Domain:</strong> Set custom domains for each instance</li>
        <li><strong>Manage Users:</strong> Add or remove admin users for each instance</li>
        <li><strong>Monitor Usage:</strong> Track resource utilization and activity</li>
        <li><strong>Suspend/Activate:</strong> Control instance availability</li>
      </ul>
      <h3>Creating a New Instance</h3>
      <ol>
        <li>Navigate to Instances in the super admin menu</li>
        <li>Click "Create New Instance"</li>
        <li>Enter the instance name and owner details</li>
        <li>Select the subscription plan</li>
        <li>Configure any custom settings</li>
        <li>Click "Create" to provision the new instance</li>
      </ol>
      <p>New instances are automatically provisioned with default settings based on the selected plan.</p>
    `,
    lastUpdated: '2024-04-01',
    isNew: false
  },
  {
    id: 'subscription-management',
    title: 'Managing Subscriptions',
    description: 'How to create and manage customer subscriptions',
    category: 'subscriptions',
    content: `
      <h2>Managing Subscriptions</h2>
      <p>The Subscriptions section allows you to manage customer subscription plans and billing.</p>
      <h3>Subscription Management Tasks</h3>
      <ul>
        <li><strong>View Subscriptions:</strong> See all active and expired subscriptions</li>
        <li><strong>Update Plans:</strong> Change a customer's subscription level</li>
        <li><strong>Process Refunds:</strong> Handle customer refund requests</li>
        <li><strong>Extend Trials:</strong> Add more time to trial accounts</li>
        <li><strong>Cancel Subscriptions:</strong> Process cancellation requests</li>
      </ul>
      <h3>Changing a Subscription Plan</h3>
      <ol>
        <li>Navigate to Subscriptions in the super admin menu</li>
        <li>Find the customer using the search function</li>
        <li>Click on their subscription details</li>
        <li>Select "Change Plan" from the actions menu</li>
        <li>Choose the new plan and effective date</li>
        <li>Click "Save Changes" to apply the update</li>
      </ol>
      <p>Plan changes can be scheduled for the next billing cycle or applied immediately with prorated billing.</p>
    `,
    lastUpdated: '2024-03-28',
    isNew: false
  },
  {
    id: 'plan-management',
    title: 'Managing Subscription Plans',
    description: 'How to create and configure subscription plans',
    category: 'plans',
    content: `
      <h2>Managing Subscription Plans</h2>
      <p>As a super admin, you can create and modify the subscription plans available to customers.</p>
      <h3>Plan Management Features</h3>
      <ul>
        <li><strong>Create Plans:</strong> Define new subscription tiers</li>
        <li><strong>Set Pricing:</strong> Configure monthly and annual pricing</li>
        <li><strong>Feature Control:</strong> Determine which features are available in each plan</li>
        <li><strong>Usage Limits:</strong> Set caps on users, storage, or other resources</li>
        <li><strong>Special Offers:</strong> Create promotional plans or discounts</li>
      </ul>
      <h3>Creating a New Plan</h3>
      <ol>
        <li>Navigate to Plans in the super admin menu</li>
        <li>Click "Create New Plan"</li>
        <li>Enter the plan name, description, and pricing details</li>
        <li>Configure the feature set and usage limits</li>
        <li>Set the display order for the pricing page</li>
        <li>Click "Save" to make the plan available</li>
      </ol>
      <p>You can preview how plans will appear on your pricing page before publishing them.</p>
    `,
    lastUpdated: '2024-04-03',
    isNew: true
  }
];

// Master admin specific guides
const masterAdminGuides: FeatureGuide[] = [
  {
    id: 'license-management',
    title: 'License Key Management',
    description: 'How to create and manage license keys',
    category: 'licenses',
    content: `
      <h2>License Key Management</h2>
      <p>As a master admin, you can create and manage license keys for your platform.</p>
      <h3>License Management Operations</h3>
      <ul>
        <li><strong>Generate Keys:</strong> Create new license keys for customers</li>
        <li><strong>Assign Domains:</strong> Restrict licenses to specific domains</li>
        <li><strong>Track Usage:</strong> Monitor license utilization</li>
        <li><strong>Revoke Licenses:</strong> Deactivate licenses when needed</li>
      </ul>
      <h3>Creating a New License Key</h3>
      <ol>
        <li>Navigate to License Keys in the master admin menu</li>
        <li>Click "Create New License"</li>
        <li>Enter the customer details and license duration</li>
        <li>Specify any domain restrictions</li>
        <li>Set feature flags and limitations</li>
        <li>Click "Generate" to create the license key</li>
      </ol>
      <p>License keys can be restricted to specific domains or IP ranges for added security.</p>
    `,
    lastUpdated: '2024-04-01',
    isNew: false
  },
  {
    id: 'super-admin-management',
    title: 'Managing Super Admins',
    description: 'How to create and manage super admin accounts',
    category: 'users',
    content: `
      <h2>Managing Super Admins</h2>
      <p>As a master admin, you can create and manage super admin accounts for your platform.</p>
      <h3>Super Admin Management Tasks</h3>
      <ul>
        <li><strong>Create Accounts:</strong> Add new super admin users</li>
        <li><strong>Set Permissions:</strong> Configure access levels</li>
        <li><strong>Monitor Activity:</strong> Track super admin actions</li>
        <li><strong>Revoke Access:</strong> Remove super admin privileges</li>
      </ul>
      <h3>Adding a New Super Admin</h3>
      <ol>
        <li>Navigate to Super Admins in the master admin menu</li>
        <li>Click "Add Super Admin"</li>
        <li>Enter the user's email address</li>
        <li>Select their permission level</li>
        <li>Assign them to specific instances if needed</li>
        <li>Click "Create Account" to send an invitation</li>
      </ol>
      <p>New super admins will receive an email invitation to set up their password and access the platform.</p>
    `,
    lastUpdated: '2024-03-15',
    isNew: false
  }
];

/**
 * Returns feature guides based on user role
 * @param role The user role (admin, superAdmin, masterAdmin)
 * @returns Array of feature guides relevant to the user role
 */
export const getAdminFeatureGuides = (role?: string): FeatureGuide[] => {
  // Base guides that all admin types can see
  let guides = [...baseAdminGuides];
  
  // Add super admin guides for superAdmin and masterAdmin roles
  if (role === 'superAdmin' || role === 'superadmin' || role === 'masterAdmin') {
    guides = [...guides, ...superAdminGuides];
  }
  
  // Add master admin guides only for masterAdmin role
  if (role === 'masterAdmin') {
    guides = [...guides, ...masterAdminGuides];
  }
  
  return guides;
};
